import { computeStandings, formatScore, type StandingRow } from '@ctd/core';
import { useState } from 'react';
import { PrintHeader } from '../components/PrintHeader';
import { RoundSelect } from '../components/RoundSelect';
import { PageHeading, PrintButton, TableRegion } from '../components/ui';
import { useSnapshot } from '../context';
import { errorText } from '../lib/download';
import { hrefFor } from '../lib/router';
import { pendingGames } from '../lib/rounds';
import { tiebreakLabel } from '../lib/settings';

/** Standings rows, or the reason they cannot be computed (an unknown tiebreak, say). */
export function useStandings(upToRound: number): { rows: StandingRow[]; error: string | null } {
  const { tournament: t } = useSnapshot();
  try {
    return { rows: computeStandings(t, { upToRound }), error: null };
  } catch (e) {
    return { rows: [], error: errorText(e) };
  }
}

export function StandingsTab() {
  const { tournament: t } = useSnapshot();
  const [chosen, setChosen] = useState<number | null>(null);
  const upTo = chosen !== null && chosen <= t.rounds.length ? chosen : t.rounds.length;
  const { rows, error } = useStandings(upTo);
  const round = t.rounds[upTo - 1];
  const pending = round === undefined ? 0 : pendingGames(round);
  const tiebreaks = t.settings.tiebreaks;
  const title = upTo === 0 ? 'Starting rank' : `Standings after round ${upTo}`;

  return (
    <section>
      <div className="page-head no-print">
        <PageHeading>{title}</PageHeading>
        <div className="actions">
          {t.rounds.length > 0 && (
            <RoundSelect rounds={t.rounds.length} value={upTo} onValue={setChosen} />
          )}
          <PrintButton label="Print standings" />
        </div>
      </div>
      <PrintHeader title={title} />
      {pending > 0 && (
        <p className="hint">
          Round {upTo} has {pending} game{pending > 1 ? 's' : ''} without a result; they count as
          not yet played.
        </p>
      )}
      {error !== null ? (
        <div className="error-box" role="alert">
          <p>Standings cannot be computed: {error}</p>
          <p>
            Fix the tiebreak list in{' '}
            <a href={hrefFor({ name: 'tournament', id: t.id, tab: 'setup' })}>Setup</a>.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <p className="empty">No players yet.</p>
      ) : (
        <TableRegion label={title}>
          <table className="data-table standings">
            <caption className="visually-hidden">{title}</caption>
            <thead>
              <tr>
                <th scope="col" className="num">
                  Rk
                </th>
                <th scope="col" className="num">
                  No.
                </th>
                <th scope="col">Name</th>
                <th scope="col" className="num">
                  Rating
                </th>
                <th scope="col" className="num">
                  Pts
                </th>
                {tiebreaks.map((code) => (
                  <th scope="col" className="num" key={code}>
                    <abbr title={tiebreakLabel(code)}>{code}</abbr>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="num">{r.rankLabel}</td>
                  <td className="num">{r.pairingNumber}</td>
                  <th scope="row">{r.name}</th>
                  <td className="num">{r.rating || '–'}</td>
                  <td className="num strong">{formatScore(r.score)}</td>
                  {r.tiebreaks.map((tb) => (
                    <td className="num" key={tb.code}>
                      {tb.display}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableRegion>
      )}
      {tiebreaks.length > 0 && (
        <dl className="legend">
          {tiebreaks.map((code) => (
            <div key={code}>
              <dt>{code}</dt>
              <dd>{tiebreakLabel(code)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
